import React from "react";

import { AssetState, AssetTypes, DamageType, RecurrentTaskType } from "__graphql__/types";
import {
  AlertIcon,
  ArrowsIcon,
  HandleBrokenIcon,
  KeyStuckIcon,
  LowCapacityIcon,
  NoQrCodeIcon,
  NotWorkingIcon,
  PhysicalDamageIcon,
  QuestionDiamondIcon,
  ToolsIcon,
} from "ui/Icons/Icons";

export const ASSET_TYPE = AssetTypes.bike_battery;
export const RECURRING_TASK_TYPE = RecurrentTaskType.bike_battery_count;

export const FUNCTIONAL_ASSET_STATES = [AssetState.asset_state_operational];

export const NON_FUNCTIONAL_ASSET_STATES = [
  AssetState.asset_state_defect,
  AssetState.asset_state_outbounded, // not used
  AssetState.asset_state_partially_operational, // not used
  AssetState.asset_state_unspecified, // not used
];

export const FULL_LIST_ASSET_STATES = [...FUNCTIONAL_ASSET_STATES, ...NON_FUNCTIONAL_ASSET_STATES];

export const VALID_REPORT_STATES = [
  AssetState.asset_state_operational,
  AssetState.asset_state_defect,
];

export const OUTBOUND_REASON_OPTIONS: {
  label: string;
  key: AssetState;
  icon: React.ReactElement<any>;
}[] = [
  { label: "Lost", key: AssetState.asset_state_lost, icon: <QuestionDiamondIcon /> },
  { label: "Stolen", key: AssetState.asset_state_stolen, icon: <AlertIcon /> },
  { label: "Repair", key: AssetState.asset_state_in_repair, icon: <ToolsIcon /> },
  { label: "Transfer", key: AssetState.asset_state_in_transit, icon: <ArrowsIcon /> },
];

export const DAMAGE_TYPE_OPTIONS: {
  label: string;
  key: DamageType;
  icon: React.ReactElement<any>;
}[] = [
  {
    label: "Not working / not charging",
    key: DamageType.damage_type_not_charging,
    icon: <NotWorkingIcon />,
  },
  {
    label: "Low capacity / drains very quickly",
    key: DamageType.damage_type_low_battery_capacity,
    icon: <LowCapacityIcon />,
  },
  {
    label: "Physical damage / dropped",
    key: DamageType.damage_type_physical_damage,
    icon: <PhysicalDamageIcon />,
  },
  {
    label: "Handle broken",
    key: DamageType.damage_type_handle_broken,
    icon: <HandleBrokenIcon />,
  },
  {
    label: "Key stuck in key hole",
    key: DamageType.damage_type_key_stuck,
    icon: <KeyStuckIcon />,
  },
  {
    label: "No QR code / no barcode",
    key: DamageType.damage_type_identifier_missing,
    icon: <NoQrCodeIcon />,
  },
];

// Reasons for a battery missing from a count. A damaged barcode is not an outbound:
// the battery stays at the hub as defect (see useReportMissingBarcode).
export const MISSING_REASON_OPTIONS: typeof OUTBOUND_REASON_OPTIONS = [
  ...OUTBOUND_REASON_OPTIONS,
  {
    label: DAMAGE_TYPE_OPTIONS.find(
      (option) => option.key === DamageType.damage_type_identifier_missing,
    )!.label,
    key: AssetState.asset_state_defect,
    icon: <NoQrCodeIcon />,
  },
];

export type SupplierName = "Smartvelo" | "Cycle" | "Mocci" | "Cooltra";
export interface BarcodeRule {
  supplier: SupplierName;
  /**
   * A pattern marks the serial number with a capture group when the code encodes more than
   * the serial number. Without a capture group, the whole barcode is the serial number.
   */
  patterns: RegExp[];
}
export const BARCODE_RULES: BarcodeRule[] = [
  {
    supplier: "Smartvelo",
    patterns: [/^B\d{11}$/, /^T\d{11}$/, /^A\d{11}$/, /^SVL-BA\d{4}$/],
  },
  {
    supplier: "Cycle",
    patterns: [/^BA\d{5}$/],
  },
  {
    supplier: "Mocci",
    patterns: [
      /^\d{9}$/,
      // Labels since the 2026 batch (Ansmann part no. 2447-0096) encode
      // "<serial>/<CIP number>/<part number>", e.g. "261903001/100142/2447-0096".
      // Only the 9-digit serial number identifies the battery.
      /^(\d{9})\/[\w-]+\/[\w-]+$/,
    ],
  },
  {
    supplier: "Cooltra",
    patterns: [/^AT\d{14}$/, /^GY\d{14}$/],
  },
];
